import { supabase } from '@/shared/lib/supabase';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';

/**
 * ticketAttachmentService — shared by the customer AND admin ticket pages
 * (W3, 2026-08-11).
 *
 * Rows are read directly (RLS: customers see own-ticket attachments minus
 * internal-note ones per migration 20260811053916; staff see all). Files are
 * NEVER touched from the browser — the `ticket-attachments` bucket is private
 * with no storage RLS, so upload and download both round-trip through the
 * ticket-attach EF (init → uploadToSignedUrl → finalize; download → signed URL).
 */

export interface TicketAttachment {
  id: string;
  ticketId: string;
  messageId: string | null;
  filename: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
}

export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.pdf';
const ALLOWED_MIME = new Set(['image/png', 'image/jpeg', 'application/pdf']);

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

async function invokeAttach<TRes>(body: Record<string, unknown>): Promise<TRes> {
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>('ticket-attach', {
    body,
  });
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = (await ctx.clone().json()) as ApiError;
        if (parsed.code && parsed.message) {
          throw new EdgeFunctionError(parsed.code, parsed.message, ctx.status, parsed.details);
        }
      } catch (parseErr) {
        if (parseErr instanceof EdgeFunctionError) throw parseErr;
      }
    }
    throw new Error(error.message);
  }
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new EdgeFunctionError(apiErr.code, apiErr.message, 200, apiErr.details);
  }
  return data as TRes;
}

export const ticketAttachmentService = {
  /** Client-side pre-check mirroring the EF/bucket limits — fail fast with a
   *  friendly message before any network round-trip. */
  validate(file: File): string | null {
    if (!ALLOWED_MIME.has(file.type)) return 'Only PNG, JPG, or PDF files can be attached.';
    if (file.size > ATTACHMENT_MAX_BYTES) return 'Maximum file size is 5 MB.';
    return null;
  },

  async list(ticketId: string): Promise<TicketAttachment[]> {
    const { data, error } = await supabase
      .from('ticket_attachment')
      .select('id, ticket_id, message_id, filename, mime_type, file_size_bytes, created_at')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });
    if (error) throw new Error(`Could not load attachments: ${error.message}`);
    return (data ?? []).map((r) => {
      const row = r as {
        id: string; ticket_id: string; message_id: string | null; filename: string;
        mime_type: string | null; file_size_bytes: number | null; created_at: string;
      };
      return {
        id: row.id,
        ticketId: row.ticket_id,
        messageId: row.message_id,
        filename: row.filename,
        mimeType: row.mime_type,
        fileSizeBytes: row.file_size_bytes,
        createdAt: row.created_at,
      };
    });
  },

  /** Full upload round-trip: init → PUT to signed URL → finalize. */
  async attach(ticketId: string, file: File, messageId?: string): Promise<string> {
    const invalid = this.validate(file);
    if (invalid) throw new Error(invalid);

    const init = await invokeAttach<{ ok: true; storage_path: string; token: string }>({
      action: 'init',
      ticket_id: ticketId,
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
    });

    const { error: upErr } = await supabase.storage
      .from('ticket-attachments')
      .uploadToSignedUrl(init.storage_path, init.token, file, { contentType: file.type });
    if (upErr) throw new Error(`Upload failed: ${upErr.message}`);

    const fin = await invokeAttach<{ ok: true; attachment_id: string }>({
      action: 'finalize',
      ticket_id: ticketId,
      storage_path: init.storage_path,
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
      ...(messageId ? { message_id: messageId } : {}),
    });
    return fin.attachment_id;
  },

  /** 600 s signed download URL (EF enforces participant + internal-note gates). */
  async getDownloadUrl(attachmentId: string): Promise<string> {
    const res = await invokeAttach<{ ok: true; url: string }>({
      action: 'download',
      attachment_id: attachmentId,
    });
    return res.url;
  },
};
